// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import {
  BackendAdType,
  VirtualCurrency,
  AdUnitID,
  AdItem,
  BannerAd,
  InterstitialAd,
  RewardedAd,
  AppKeys,
  AdsConfig
} from '../types/adManagement';

export class VirtualCurrencyClass implements VirtualCurrency {
  type: number;
  amount: number;

  constructor(type: number = 2, amount: number = 0) {
    this.type = type;
    this.amount = amount;
  }

  /**
   * Create from plain object
   */
  static fromObject(obj: any): VirtualCurrencyClass {
    return new VirtualCurrencyClass(obj.type || 2, obj.amount || 0);
  }

  /**
   * Convert to plain object
   */
  toObject(): VirtualCurrency {
    return {
      type: this.type,
      amount: this.amount
    };
  }

  /**
   * Create a copy of this currency
   */
  clone(): VirtualCurrencyClass {
    return new VirtualCurrencyClass(this.type, this.amount);
  }

  /**
   * Check if this currency is valid
   */
  isValid(): boolean {
    return this.amount >= 0 && this.type > 0;
  }
}

export class AdUnitIDClass implements AdUnitID {
  android: string;
  ios: string;

  constructor(android: string = '', ios: string = '') {
    this.android = android;
    this.ios = ios;
  }

  /**
   * Create from plain object
   */
  static fromObject(obj: any): AdUnitIDClass {
    return new AdUnitIDClass(obj.android || '', obj.ios || '');
  }

  /**
   * Convert to plain object
   */
  toObject(): AdUnitID {
    return {
      android: this.android,
      ios: this.ios
    };
  }

  /**
   * Create a copy of this ad unit ID
   */
  clone(): AdUnitIDClass {
    return new AdUnitIDClass(this.android, this.ios);
  }

  /**
   * Check if at least one platform has an ad unit ID
   */
  hasAnyId(): boolean {
    return !!(this.android || this.ios);
  }
}

export class AdItemClass implements AdItem {
  enabled: boolean;
  type: BackendAdType;
  ad_unit_id: AdUnitIDClass;

  constructor(
    enabled: boolean = true,
    type: BackendAdType = BackendAdType.Rewarded,
    ad_unit_id?: AdUnitIDClass
  ) {
    this.enabled = enabled;
    this.type = type;
    this.ad_unit_id = ad_unit_id || new AdUnitIDClass();
  }

  /**
   * Create from plain object
   */
  static fromObject(obj: any): AdItemClass {
    return new AdItemClass(
      obj.enabled !== undefined ? obj.enabled : true,
      obj.type !== undefined ? obj.type : BackendAdType.Rewarded,
      obj.ad_unit_id ? AdUnitIDClass.fromObject(obj.ad_unit_id) : new AdUnitIDClass()
    );
  }

  /**
   * Convert to plain object
   */
  toObject(): AdItem {
    return {
      enabled: this.enabled,
      type: this.type,
      ad_unit_id: this.ad_unit_id.toObject()
    };
  }

  /**
   * Create a copy of this ad item
   */
  clone(): AdItemClass {
    return new AdItemClass(this.enabled, this.type, this.ad_unit_id.clone());
  }

  /**
   * Check if this ad item is valid
   */
  isValid(): boolean {
    return this.ad_unit_id.hasAnyId();
  }
}

export class BannerAdClass extends AdItemClass implements BannerAd {
  constructor(
    enabled: boolean = true,
    ad_unit_id?: AdUnitIDClass
  ) {
    super(enabled, BackendAdType.Banner, ad_unit_id);
  }

  /**
   * Create from plain object
   */
  static fromObject(obj: any): BannerAdClass {
    const banner = new BannerAdClass();
    banner.enabled = obj.enabled !== undefined ? obj.enabled : true;
    banner.type = BackendAdType.Banner;
    banner.ad_unit_id = obj.ad_unit_id ? AdUnitIDClass.fromObject(obj.ad_unit_id) : new AdUnitIDClass();
    return banner;
  }

  /**
   * Convert to plain object
   */
  toObject(): BannerAd {
    return {
      enabled: this.enabled,
      type: this.type,
      ad_unit_id: this.ad_unit_id.toObject()
    };
  }

  /**
   * Create a copy of this banner ad
   */
  clone(): BannerAdClass {
    return new BannerAdClass(this.enabled, this.ad_unit_id.clone());
  }
}

export class InterstitialAdClass extends AdItemClass implements InterstitialAd {
  play_frequency: number;

  constructor(
    enabled: boolean = true,
    ad_unit_id?: AdUnitIDClass,
    play_frequency: number = 3
  ) {
    super(enabled, BackendAdType.Interstitial, ad_unit_id);
    this.play_frequency = play_frequency;
  }

  /**
   * Create from plain object
   */
  static fromObject(obj: any): InterstitialAdClass {
    const interstitial = new InterstitialAdClass();
    interstitial.enabled = obj.enabled !== undefined ? obj.enabled : true;
    interstitial.type = BackendAdType.Interstitial;
    interstitial.ad_unit_id = obj.ad_unit_id ? AdUnitIDClass.fromObject(obj.ad_unit_id) : new AdUnitIDClass();
    interstitial.play_frequency = obj.play_frequency !== undefined ? obj.play_frequency : 3;
    return interstitial;
  }

  /**
   * Convert to plain object
   */
  toObject(): InterstitialAd {
    return {
      enabled: this.enabled,
      type: this.type,
      ad_unit_id: this.ad_unit_id.toObject(),
      play_frequency: this.play_frequency
    };
  }

  /**
   * Create a copy of this interstitial ad
   */
  clone(): InterstitialAdClass {
    return new InterstitialAdClass(this.enabled, this.ad_unit_id.clone(), this.play_frequency);
  }

  /**
   * Check if this interstitial ad is valid
   */
  isValid(): boolean {
    return super.isValid() && this.play_frequency > 0;
  }
}

export class RewardedAdClass extends AdItemClass implements RewardedAd {
  rewards: VirtualCurrencyClass[];

  constructor(
    enabled: boolean = true,
    ad_unit_id?: AdUnitIDClass,
    rewards?: VirtualCurrencyClass[]
  ) {
    super(enabled, BackendAdType.Rewarded, ad_unit_id);
    this.rewards = rewards || [new VirtualCurrencyClass(2, 50)];
  }

  /**
   * Create from plain object
   */
  static fromObject(obj: any): RewardedAdClass {
    const rewarded = new RewardedAdClass();
    rewarded.enabled = obj.enabled !== undefined ? obj.enabled : true;
    rewarded.type = BackendAdType.Rewarded;
    rewarded.ad_unit_id = obj.ad_unit_id ? AdUnitIDClass.fromObject(obj.ad_unit_id) : new AdUnitIDClass();
    
    // Convert rewards
    rewarded.rewards = [];
    if (obj.rewards && Array.isArray(obj.rewards)) {
      rewarded.rewards = obj.rewards.map((reward: any) => VirtualCurrencyClass.fromObject(reward));
    } else {
      rewarded.rewards = [new VirtualCurrencyClass(2, 50)];
    }
    
    return rewarded;
  }

  /**
   * Convert to plain object
   */
  toObject(): RewardedAd {
    return {
      enabled: this.enabled,
      type: this.type,
      ad_unit_id: this.ad_unit_id.toObject(),
      rewards: this.rewards.map(reward => reward.toObject())
    };
  }

  /**
   * Create a copy of this rewarded ad
   */
  clone(): RewardedAdClass {
    return new RewardedAdClass(
      this.enabled,
      this.ad_unit_id.clone(),
      this.rewards.map(reward => reward.clone())
    );
  }

  /**
   * Add a new reward
   */
  addReward(reward: VirtualCurrencyClass): void {
    this.rewards.push(reward);
  }

  /**
   * Remove a reward by index
   */
  removeReward(index: number): void {
    if (index >= 0 && index < this.rewards.length) {
      this.rewards.splice(index, 1);
    }
  }

  /**
   * Get total reward value for a specific currency type
   */
  getTotalRewardValue(currencyType: number): number {
    return this.rewards
      .filter(reward => reward.type === currencyType)
      .reduce((total, reward) => total + reward.amount, 0);
  }

  /**
   * Check if this rewarded ad is valid
   */
  isValid(): boolean {
    return super.isValid() && this.rewards.length > 0 && this.rewards.every(reward => reward.isValid());
  }
}

export class AppKeysClass implements AppKeys {
  android: string;
  ios: string;

  constructor(android: string = '', ios: string = '') {
    this.android = android;
    this.ios = ios;
  }

  /**
   * Create from plain object
   */
  static fromObject(obj: any): AppKeysClass {
    return new AppKeysClass(obj.android || '', obj.ios || '');
  }

  /**
   * Convert to plain object
   */
  toObject(): AppKeys {
    return {
      android: this.android,
      ios: this.ios
    };
  }

  /**
   * Create a copy of this app keys
   */
  clone(): AppKeysClass {
    return new AppKeysClass(this.android, this.ios);
  }

  /**
   * Check if at least one platform has an app key
   */
  hasAnyKey(): boolean {
    return !!(this.android || this.ios);
  }
}

export class AdsSettingsClass implements AdsConfig {
  app_keys: AppKeysClass;
  test_mode: boolean;
  rewarded: RewardedAdClass;
  interstitial: InterstitialAdClass;
  banner: BannerAdClass;

  constructor(
    app_keys?: AppKeysClass,
    test_mode: boolean = false,
    rewarded?: RewardedAdClass,
    interstitial?: InterstitialAdClass,
    banner?: BannerAdClass
  ) {
    this.app_keys = app_keys || new AppKeysClass();
    this.test_mode = test_mode;
    this.rewarded = rewarded || new RewardedAdClass();
    this.interstitial = interstitial || new InterstitialAdClass();
    this.banner = banner || new BannerAdClass();
  }

  /**
   * Serialize to JSON string
   */
  toJSON(): string {
    try {
      return JSON.stringify(this.toObject(), null, 2);
    } catch (error) {
      console.error('Serialization failed:', error);
      return JSON.stringify({
        app_keys: this.app_keys,
        test_mode: this.test_mode,
        rewarded: this.rewarded,
        interstitial: this.interstitial,
        banner: this.banner
      }, null, 2);
    }
  }

  /**
   * Deserialize from JSON string
   */
  static fromJSON(jsonString: string): AdsSettingsClass {
    try {
      const plainObject = JSON.parse(jsonString);
      return AdsSettingsClass.fromObject(plainObject);
    } catch (error) {
      console.error('JSON deserialization failed:', error);
      return new AdsSettingsClass(); // Return default settings on error
    }
  }

  /**
   * Create from plain object
   */
  static fromObject(obj: any): AdsSettingsClass {
    try {
      const instance = new AdsSettingsClass();
      
      if (obj.app_keys) {
        instance.app_keys = AppKeysClass.fromObject(obj.app_keys);
      }
      
      instance.test_mode = obj.test_mode !== undefined ? obj.test_mode : false;
      
      if (obj.rewarded) {
        instance.rewarded = RewardedAdClass.fromObject(obj.rewarded);
      }
      
      if (obj.interstitial) {
        instance.interstitial = InterstitialAdClass.fromObject(obj.interstitial);
      }
      
      if (obj.banner) {
        instance.banner = BannerAdClass.fromObject(obj.banner);
      }
      
      return instance;
    } catch (error) {
      console.error('Object conversion failed:', error);
      return new AdsSettingsClass(); // Return default settings on error
    }
  }

  /**
   * Convert to plain object
   */
  toObject(): AdsConfig {
    try {
      return {
        app_keys: this.app_keys.toObject(),
        test_mode: this.test_mode,
        rewarded: this.rewarded.toObject(),
        interstitial: this.interstitial.toObject(),
        banner: this.banner.toObject()
      };
    } catch (error) {
      console.error('Object conversion failed:', error);
      // Fallback to basic conversion
      return {
        app_keys: this.app_keys.toObject(),
        test_mode: this.test_mode,
        rewarded: this.rewarded.toObject(),
        interstitial: this.interstitial.toObject(),
        banner: this.banner.toObject()
      };
    }
  }

  /**
   * Create a deep copy of the settings
   */
  clone(): AdsSettingsClass {
    return new AdsSettingsClass(
      this.app_keys.clone(),
      this.test_mode,
      this.rewarded.clone(),
      this.interstitial.clone(),
      this.banner.clone()
    );
  }

  /**
   * Check if settings have changed compared to another instance
   */
  hasChangedFrom(other: AdsSettingsClass): boolean {
    return JSON.stringify(this.toObject()) !== JSON.stringify(other.toObject());
  }

  /**
   * Update from another AdsSettingsClass instance
   */
  updateFrom(other: AdsSettingsClass): void {
    this.app_keys = other.app_keys.clone();
    this.test_mode = other.test_mode;
    this.rewarded = other.rewarded.clone();
    this.interstitial = other.interstitial.clone();
    this.banner = other.banner.clone();
  }

  /**
   * Get statistics about the configuration
   */
  getStats(): {
    totalAdTypes: number;
    enabledAdTypes: number;
    totalRewards: number;
    hasAppKeys: boolean;
  } {
    const adTypes = [this.rewarded, this.interstitial, this.banner];
    const enabledAdTypes = adTypes.filter(ad => ad.enabled);
    
    return {
      totalAdTypes: adTypes.length,
      enabledAdTypes: enabledAdTypes.length,
      totalRewards: this.rewarded.rewards.length,
      hasAppKeys: this.app_keys.hasAnyKey()
    };
  }

  /**
   * Validate the configuration
   */
  validate(): string[] {
    const errors: string[] = [];
    
    if (!this.app_keys.hasAnyKey()) {
      errors.push('At least one app key (Android or iOS) must be configured');
    }
    
    if (this.rewarded.enabled && !this.rewarded.isValid()) {
      errors.push('Rewarded ad is enabled but has invalid configuration');
    }
    
    if (this.interstitial.enabled && !this.interstitial.isValid()) {
      errors.push('Interstitial ad is enabled but has invalid configuration');
    }
    
    if (this.banner.enabled && !this.banner.isValid()) {
      errors.push('Banner ad is enabled but has invalid configuration');
    }
    
    return errors;
  }

  /**
   * Check if all settings are valid
   */
  isValid(): boolean {
    return this.validate().length === 0;
  }
}

